import React, { useState } from 'react';
import { M2Row, M2_COLUMNS, M2_FILTERS, M2SearchContext, M2_SEARCH, M2_VALIDATE, M2_PREPARE_SEARCH } from '../configs/m2.config';
import Modal from './Modal';
import styles from './Modal.module.css';
import headerStyles from '../components/Header.module.css';
import Filter from '../components/Filter';
import { resolveFoilAvailability, resolveRowDetails, indexRows, filterSelectedRows } from '../util/rows';
import { useProductModel } from '../util/useProductModel';
import { filterRows } from '../util/filter';
import { fetchBatches, excludeCurrentBatch } from '../util/batch';
import HeaderButtons from '../components/HeaderButtons';
import FoilTable from '../components/FoilTable';
import Footer from '../components/Footer';
import HeaderField from '../components/HeaderField';
import { useReloadable } from '../util/useReloadable';
import ErrorMessage from '../components/ErrorMessage';
import { resolvePastedFoilNumbers } from '../util/clipboard';


const M2 = ({ createMode }: { createMode: boolean }) => {
    const [product, updateProduct, productRef, initialProductRef] = useProductModel();
    const [rows, setRows] = useState<M2Row[]>([]);
    const [indexedRows, setIndexedRows] = useState<{ [foilNumber: string]: M2Row }>();

    const [query, setQuery] = useState<string>('');
    const [activeFilters, setActiveFilters] = useState<number[]>([]);

    const { loading, reload, error } = useReloadable(async () => {
        const [inputBatches, existingOutputBatches] = await fetchBatches(['^S-', '^N-']);
        const outputBatches = excludeCurrentBatch(existingOutputBatches, productRef.current?.identifier);

        const rows: M2Row[] = resolveFoilAvailability<M2Row>(inputBatches, outputBatches, 'foils');
        const availableRows = rows.filter((row) => row.available);
        const detailedRows: M2Row[] = await resolveRowDetails(availableRows, M2_COLUMNS, inputBatches);

        setRows(detailedRows);
        setIndexedRows(indexRows(detailedRows));
    }, []);

    const batchNumber = product?.identifier || '';
    const visibleRows = filterRows<M2Row, M2SearchContext>(rows, M2_FILTERS, M2_SEARCH, activeFilters, M2_PREPARE_SEARCH(query));
    const selectedRowKeys = product?.parameters?.foils || [];
    const selectedRows = filterSelectedRows(selectedRowKeys, indexedRows);
    const hasSelectedRows = selectedRows.length > 0;

    const updateSelectedRowKeys = (selectedRowKeys: string[]) => {
        updateProduct({
            parameters: {
                foils: selectedRowKeys,
            }
        });
    }

    const updateBatchNumber = (newBatchNumber: string) => {
        updateProduct({
            identifier: newBatchNumber
        });
    }

    const handlePasteFoils = async (foilNumbers: string[]) => {
        const newSelectedRowKeys = await resolvePastedFoilNumbers(foilNumbers, selectedRowKeys, [], indexedRows);

        if (!newSelectedRowKeys) {
            return;
        }

        updateSelectedRowKeys(newSelectedRowKeys);
    }

    if (error) {
        return (
            <ErrorMessage error={error} />
        )
    }

    return (
        <Modal
            loading={loading}
            onBeforeSubmit={() => M2_VALIDATE(product, createMode, initialProductRef.current)}
        >
            <header className={headerStyles.topHeader}>
                <HeaderField
                    id={'batchNumber'}
                    label={'Batch number'}
                    value={batchNumber}
                    onChange={updateBatchNumber}
                />
            </header>
            <header className={headerStyles.bottomHeader}>
                <Filter
                    query={query}
                    setQuery={setQuery}
                    filters={M2_FILTERS}
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                />
                <HeaderButtons<M2Row>
                    columns={M2_COLUMNS}
                    rows={rows}
                    exportFilename={'Azumuta_export_M2'}
                    onPasteFoils={handlePasteFoils}
                    onReload={reload}
                />
            </header>
            <section className={styles.inputTableWrapper}>
                <FoilTable<M2Row>
                    columns={M2_COLUMNS}
                    rows={visibleRows}
                    selectedRowKeys={selectedRowKeys}
                    sortable={true}
                    onRowToggle={updateSelectedRowKeys}
                />
            </section>
            {hasSelectedRows &&
                <section className={styles.outputTableWrapper}>
                    <FoilTable<M2Row>
                        columns={M2_COLUMNS}
                        rows={selectedRows}
                        selectedRowKeys={selectedRowKeys}
                        onRowToggle={updateSelectedRowKeys}
                    />
                </section>
            }
            {hasSelectedRows &&
                <Footer
                    selectedRowCount={selectedRowKeys.length}
                    onClear={() => updateSelectedRowKeys([])}
                />
            }
        </Modal>
    );
}

export default M2;

